import { supabase } from "@/integrations/supabase/client";
import { MatchSuggestion, ManualMatchSearchResult, OwnAccountCandidate, PaginatedTransactionResult, TransactionQueryFilters } from "@/types/payments";
import { handleError } from '@/utils/handleError';
import { settledRefusalOrNull } from "@/utils/errorUtils";
import { translateOutsideReact } from '@/i18n/translateOutsideReact';

export class TransactionApiClient {
  /**
   * Get a page of transactions across all of the company's accounts (issue #363).
   * The persisted store is read from the DB, so a single paginated, newest-first list
   * replaces the former per-account grouping. `filters` (issue #363 review follow-up) are
   * pushed down to the DB query — see transaction-api/index.ts's getTransactionFilters.
   */
  static async getAllTransactions(
    page: number,
    pageSize: number,
    filters?: TransactionQueryFilters,
  ): Promise<PaginatedTransactionResult> {
    try {
      const { data, error } = await supabase.functions.invoke("transaction-api", {
        method: "POST",
        body: { action: "getAllTransactions", page, pageSize, ...filters },
      });

      if (error) throw error;

      return data as PaginatedTransactionResult;
    } catch (error) {
      await handleError(error, translateOutsideReact('consultantErrors.fallback.failedToFetchTransactions'), 'getAllTransactions');
      throw error;
    }
  }

  /**
   * Get a page of transactions for a specific account (issue #363).
   */
  static async getTransactionsForAccount(
    accountId: string,
    page: number,
    pageSize: number,
    filters?: TransactionQueryFilters,
  ): Promise<PaginatedTransactionResult> {
    try {
      const { data, error } = await supabase.functions.invoke("transaction-api", {
        method: "POST",
        body: { action: "getTransactionsForAccount", accountId, page, pageSize, ...filters },
      });

      if (error) throw error;

      return data as PaginatedTransactionResult;
    } catch (error) {
      await handleError(error, translateOutsideReact('consultantErrors.fallback.failedToFetchTransactionsForAccount'), 'getTransactionsForAccount');
      throw error;
    }
  }

  /**
   * Get the background sync status for a single account (issue #363 follow-up — connect-time
   * sync). Used to poll for completion after a bank connection so the UI can refresh the
   * transaction list once the first sync finishes, without the user having to refresh manually.
   */
  static async getSyncStatus(accountId: string): Promise<{
    status: "pending" | "processing" | "completed" | "failed" | null;
    lastSuccessfulFetchAt: string | null;
  }> {
    try {
      const { data, error } = await supabase.functions.invoke("transaction-api", {
        method: "POST",
        body: { action: "getSyncStatus", accountId },
      });

      if (error) throw error;

      return data as { status: "pending" | "processing" | "completed" | "failed" | null; lastSuccessfulFetchAt: string | null };
    } catch (error) {
      await handleError(error, translateOutsideReact('consultantErrors.fallback.failedToFetchSyncStatus'), 'getSyncStatus');
      throw error;
    }
  }

  /**
   * Batched sync status for every one of the active company's bank accounts, in one call
   * (FIN-568 — bank-account-importing-indicator). RLS-scoped to the caller's company —
   * no accountId param, unlike getSyncStatus above.
   */
  static async getSyncStatusForCompany(): Promise<Array<{
    bankAccountId: string;
    status: "pending" | "processing" | "completed" | "failed";
    lastSuccessfulFetchAt: string | null;
    // Retry budget — lets the client distinguish a failure still being retried from one the
    // scheduler has permanently abandoned (FIN-745).
    retryCount: number;
    maxRetries: number;
  }>> {
    try {
      const { data, error } = await supabase.functions.invoke("transaction-api", {
        method: "POST",
        body: { action: "getSyncStatusForCompany" },
      });

      if (error) throw error;

      return (data?.statuses ?? []) as Array<{
        bankAccountId: string;
        status: "pending" | "processing" | "completed" | "failed";
        lastSuccessfulFetchAt: string | null;
        retryCount: number;
        maxRetries: number;
      }>;
    } catch (error) {
      await handleError(error, translateOutsideReact('consultantErrors.fallback.failedToFetchCompanySyncStatus'), 'getSyncStatusForCompany');
      throw error;
    }
  }

  /**
   * Get match suggestions for transactions
   */
  static async getMatchSuggestions(transactionId: string, accountId: string): Promise<MatchSuggestion[]> {
    try {
      const { data, error } = await supabase.functions.invoke("transaction-api", {
        method: "POST",
        body: { action: "getMatchSuggestions", transactionId, accountId },
      });

      if (error) throw error;

      return data.matchSuggestions || [];
    } catch (error) {
      await handleError(error, translateOutsideReact('consultantErrors.fallback.failedToFetchMatchSuggestions'), 'getMatchSuggestions');
      throw error;
    }
  }

  /**
   * Precomputed candidate matches for a transaction (issue #457) — reads the async match_jobs
   * pipeline's already-computed auto_match_decisions suggestion instead of a live JIT scoring
   * query. No accountId needed (unlike getMatchSuggestions above). Returns a documentType-
   * discriminated tagged union (issue #497) — receivables for a credit transaction, payables for
   * a debit transaction.
   */
  static async getPrecomputedMatchSuggestions(transactionId: string): Promise<MatchSuggestion[]> {
    try {
      const { data, error } = await supabase.functions.invoke("transaction-api", {
        method: "POST",
        body: { action: "getPrecomputedMatchSuggestions", transactionId },
      });

      if (error) throw error;

      return data.matchSuggestions || [];
    } catch (error) {
      await handleError(error, translateOutsideReact('consultantErrors.fallback.failedToFetchPrecomputedMatchSuggestions'), 'getPrecomputedMatchSuggestions');
      throw error;
    }
  }

  /**
   * "Manually Match" free-text search fallback (product-feedback-30-aug.F4, issue #904).
   * `direction` mirrors the suggestion modal's own direction gating (debit -> payables,
   * credit -> receivables) — never both.
   */
  static async searchUnmatchedDocuments(
    direction: "debit" | "credit",
    query: string,
  ): Promise<ManualMatchSearchResult[]> {
    try {
      const { data, error } = await supabase.functions.invoke("transaction-api", {
        method: "POST",
        body: { action: "searchUnmatchedDocuments", direction, query },
      });

      if (error) throw error;

      return data.results || [];
    } catch (error) {
      await handleError(error, translateOutsideReact('consultantErrors.fallback.failedToSearchUnmatchedDocuments'), 'searchUnmatchedDocuments');
      throw error;
    }
  }

  /** FIN-976 F6: the tax account rows Manually Match offers for this bank transaction. */
  static async listOwnAccountCandidates(bankTransactionId: string): Promise<OwnAccountCandidate[]> {
    const { data, error } = await supabase.functions.invoke("transaction-api", {
      method: "POST",
      body: { action: "listOwnAccountCandidates", bankTransactionId },
    });
    if (error) {
      await handleError(error, translateOutsideReact('consultantErrors.fallback.failedToLoadMatchSuggestions'), 'listOwnAccountCandidates');
      throw error;
    }
    return data.candidates || [];
  }

  /**
   * FIN-976 F6: pairs the bank transaction with a tax account row. A named refusal (already
   * settled, amounts differ) is thrown for the caller to title, as confirmPayableMatch does.
   */
  static async confirmOwnAccountTransfer(
    bankTransactionId: string,
    candidate: Pick<OwnAccountCandidate, "legKind" | "legId">,
  ): Promise<{ decisionId: string }> {
    const { data, error } = await supabase.functions.invoke("transaction-api", {
      method: "POST",
      body: { action: "confirmOwnAccountTransfer", bankTransactionId, legKind: candidate.legKind, legId: candidate.legId },
    });
    if (error) {
      const { settled, body } = await settledRefusalOrNull(error);
      if (settled) throw settled;
      await handleError(error, translateOutsideReact('consultantErrors.fallback.failedToConfirmMatch'), 'confirmOwnAccountTransfer', body);
      throw error;
    }
    return data as { decisionId: string };
  }

  /**
   * FIN-1567 PR5 (stub -- the real booking feature is FIN-1575): requests marking (or undoing)
   * one or more personal-account lines as private. Always resolves to `{ available: false }` on
   * success -- nothing is booked yet, the server only validates the request and logs demand.
   */
  static async markTransactionsPrivate(
    bankTransactionIds: string[],
    intent: "mark" | "undo",
  ): Promise<{ available: false }> {
    const { data, error } = await supabase.functions.invoke("transaction-api", {
      method: "POST",
      body: {
        action: intent === "mark" ? "markTransactionsPrivate" : "undoPrivateMark",
        bankTransactionIds,
      },
    });
    if (error) {
      await handleError(error, translateOutsideReact('consultantErrors.fallback.failedToMarkTransactionPrivate'), "markTransactionsPrivate");
      throw error;
    }
    return data as { available: false };
  }
}
